import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiPlus, FiShield, FiTrash2, FiUserCheck, FiUserX } from "react-icons/fi";
import VmsPageCard from "../components/VmsPageCard";
import { VMS_COLORS } from "../constants/theme";
import { vmsApi } from "../services/vmsApi";

const VmsSecurityUsers = () => {
  const toast = useToast();
  const [emailsInput, setEmailsInput] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [busyId, setBusyId] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const parsedEmails = useMemo(
    () =>
      emailsInput
        .split(/[,\n;]/)
        .map((v) => v.trim())
        .filter(Boolean),
    [emailsInput]
  );

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await vmsApi.listSecurityUsers();
      const list = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setUsers(list);
    } catch (err) {
      toast({ title: "Failed to load security users", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers().catch(() => {});
  }, []);

  const createUsers = async () => {
    if (!parsedEmails.length) {
      toast({ title: "Email required", description: "Enter at least one email.", status: "warning" });
      return;
    }
    setIsCreating(true);
    try {
      const res = await vmsApi.createSecurityUsers(parsedEmails);
      setEmailsInput("");
      await loadUsers();
      const resultCount = Array.isArray(res?.results) ? res.results.length : parsedEmails.length;
      toast({ title: "Security user processing complete", description: `${resultCount} email(s) processed.`, status: "success" });
      onClose();
    } catch (err) {
      toast({ title: "Create failed", description: err?.message, status: "error" });
    } finally {
      setIsCreating(false);
    }
  };

  const changeStatus = async (user, action) => {
    const userId = String(user?.user_id || user?.id || "");
    if (!userId) return;
    setBusyId(userId);
    try {
      await vmsApi.updateSecurityUserStatus(userId, action);
      await loadUsers();
      toast({ title: `User ${action}d`, status: "success" });
    } catch (err) {
      toast({ title: "Status update failed", description: err?.message, status: "error" });
    } finally {
      setBusyId("");
    }
  };

  const deleteUser = async (user) => {
    const userId = String(user?.user_id || user?.id || "");
    if (!userId) return;
    const confirmed = window.confirm(`Delete security user ${user?.email || userId}?`);
    if (!confirmed) return;
    setBusyId(userId);
    try {
      await vmsApi.deleteSecurityUser(userId);
      await loadUsers();
      toast({ title: "Security user deleted", status: "success" });
    } catch (err) {
      toast({ title: "Delete failed", description: err?.message, status: "error" });
    } finally {
      setBusyId("");
    }
  };

  return (
    <VStack align="stretch" spacing={5}>
      <VmsPageCard>
        <HStack justify="space-between" mb={4}>
          <Box>
            <Text fontSize="xl" fontWeight="800" color="gray.800">
              Security Officers
            </Text>
            <Text color="gray.500" fontSize="sm">
              Create and manage security officer accounts.
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            bg={VMS_COLORS.primary}
            color="white"
            _hover={{ bg: VMS_COLORS.primaryDark }}
            onClick={onOpen}
          >
            Create Security Officer
          </Button>
        </HStack>

        <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((user) => {
                const userId = String(user?.user_id || user?.id || "");
                const status = String(user?.status || "").toLowerCase();
                const isActive = status === "active";
                return (
                  <Tr key={userId || user?.email}>
                    <Td>{user?.email || "-"}</Td>
                    <Td>{user?.name || "-"}</Td>
                    <Td>
                      <Badge colorScheme={isActive ? "green" : "orange"}>{status || "unknown"}</Badge>
                    </Td>
                    <Td textAlign="right">
                      <HStack justify="flex-end">
                        {isActive ? (
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<FiUserX />}
                            onClick={() => changeStatus(user, "deactivate")}
                            isLoading={busyId === userId}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<FiUserCheck />}
                            onClick={() => changeStatus(user, "activate")}
                            isLoading={busyId === userId}
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          leftIcon={<FiTrash2 />}
                          onClick={() => deleteUser(user)}
                          isLoading={busyId === userId}
                        >
                          Delete
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
              {!isLoading && !users.length ? (
                <Tr>
                  <Td colSpan={4}>
                    <Text py={8} textAlign="center" color="gray.500">
                      No security users yet.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </TableContainer>

        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent borderRadius="16px">
            <ModalHeader>Create Security Officer</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={2} fontSize="sm" color="gray.700">
                Enter one or more emails (comma, semicolon, or new line separated).
              </Text>
              <Input
                value={emailsInput}
                onChange={(e) => setEmailsInput(e.target.value)}
                placeholder="e.g. security1@example.com, security2@example.com"
                bg="white"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                leftIcon={<FiShield />}
                bg={VMS_COLORS.primary}
                color="white"
                _hover={{ bg: VMS_COLORS.primaryDark }}
                onClick={createUsers}
                isLoading={isCreating}
              >
                Create
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VmsPageCard>
    </VStack>
  );
};

export default VmsSecurityUsers;
